import React, { useEffect, useState } from 'react';
import DateTimeCard from './DateTimeCard';
import PrayerTimesCard from './PrayerTimesCard';
import DailyDuaCard from './DailyDuaCard';
import DailyHadithCard from './DailyHadithCard';
import QuranProgressCard from './QuranProgressCard';
import AdhkarTrackerCard from './AdhkarTrackerCard';
import GoalsCard from './GoalsCard';
import QuranGuestCard from './QuranGuestCard';
import { Quran01Icon } from 'hugeicons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSettings } from '../../contexts/SettingsContext';

const DesktopLayout = ({ 
  currentUser, 
  isDarkMode, 
  dashboardData, 
  greeting, 
  setDashboardData,
  isLoading,
  isInitialized
}) => {
  const { settings, isInitialized: settingsInitialized } = useSettings();
  const [showGuestProgress, setShowGuestProgress] = useState(() => {
    // If localStorage says to show progress, do so
    return localStorage.getItem('showQuranProgressAsGuest') === 'true';
  });
  
  const handleGuestNoThanks = () => {
    localStorage.setItem('showQuranProgressAsGuest', 'true');
    setShowGuestProgress(true);
  };
  
  const handleToggleAdhkar = (prayer) => {
    setDashboardData(prevData => ({
      ...prevData,
      adhkarCompleted: {
        ...prevData.adhkarCompleted,
        [prayer]: !prevData.adhkarCompleted[prayer]
      }
    }));
  };

  const handleNextDua = () => {
    console.log('[DesktopLayout] handleNextDua called', {
      currentIndex: dashboardData?.currentDuaIndex,
      duasLength: dashboardData?.duas?.length
    });
    if (!dashboardData?.duas?.length) {
      console.warn('[DesktopLayout] Cannot navigate - no duas available');
      return;
    }
    const newDashboardData = {
      ...dashboardData,
      currentDuaIndex: (dashboardData.currentDuaIndex + 1) % dashboardData.duas.length
    };
    console.log('[DesktopLayout] Setting new dua index:', newDashboardData.currentDuaIndex);
    setDashboardData(newDashboardData);
  };

  const handlePreviousDua = () => {
    console.log('[DesktopLayout] handlePreviousDua called', {
      currentIndex: dashboardData?.currentDuaIndex,
      duasLength: dashboardData?.duas?.length
    });
    if (!dashboardData?.duas?.length) {
      console.warn('[DesktopLayout] Cannot navigate - no duas available');
      return;
    }
    const newDashboardData = {
      ...dashboardData,
      currentDuaIndex: (dashboardData.currentDuaIndex - 1 + dashboardData.duas.length) % dashboardData.duas.length
    };
    console.log('[DesktopLayout] Setting new dua index:', newDashboardData.currentDuaIndex);
    setDashboardData(newDashboardData);
  };

  const handleNextHadith = () => {
    console.log('[DesktopLayout] handleNextHadith called', {
      currentIndex: dashboardData?.currentHadithIndex,
      hadithsLength: dashboardData?.hadiths?.length
    });
    if (!dashboardData?.hadiths?.length) {
      console.warn('[DesktopLayout] Cannot navigate - no hadiths available');
      return;
    }
    const newDashboardData = {
      ...dashboardData,
      currentHadithIndex: (dashboardData.currentHadithIndex + 1) % dashboardData.hadiths.length
    };
    console.log('[DesktopLayout] Setting new hadith index:', newDashboardData.currentHadithIndex);
    setDashboardData(newDashboardData);
  };

  const handlePreviousHadith = () => {
    console.log('[DesktopLayout] handlePreviousHadith called', {
      currentIndex: dashboardData?.currentHadithIndex,
      hadithsLength: dashboardData?.hadiths?.length
    });
    if (!dashboardData?.hadiths?.length) {
      console.warn('[DesktopLayout] Cannot navigate - no hadiths available');
      return;
    }
    const newDashboardData = {
      ...dashboardData,
      currentHadithIndex: (dashboardData.currentHadithIndex - 1 + dashboardData.hadiths.length) % dashboardData.hadiths.length
    };
    console.log('[DesktopLayout] Setting new hadith index:', newDashboardData.currentHadithIndex);
    setDashboardData(newDashboardData);
  };

  // Debug log to verify card styling and background interaction
  useEffect(() => {
    console.log('DesktopLayout rendering with:', {
      isDarkMode,
      cardStyle: isDarkMode 
        ? 'bg-white/5 border border-white/10' 
        : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)] border border-black/5',
      welcomeSectionStyle: 'bg-black with widebgpattern',
      backgroundRendering: 'expecting transparent background from Dashboard component'
    });
  }, [isDarkMode]);

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      {/* Welcome Section with DateTime - Styling kept dark for both modes for contrast */}
      <div className="pt-4 pb-8">
        <div className="relative overflow-hidden bg-black p-8 rounded-[32px] transition-all duration-200">
          <div className="absolute inset-0">
            <img
              src="/images/widebgpattern.png" 
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-[0.15]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#042f21] via-[#042f21]/80 to-[#042f21]/90"></div>
          </div>
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:justify-between lg:items-start">
            <div className="w-full lg:w-auto text-left lg:text-right order-1 lg:order-none mb-8 lg:mb-0">
              <h1 className="text-lg lg:text-2xl font-medium text-white">
                {currentUser.displayName}    ٱلسَّلَامُ عَلَيْكمْ
              </h1>
              <p className="mt-2 text-xs lg:text-sm text-white/80">
                {greeting}
              </p>
            </div>
            
            <div className="w-full lg:w-auto order-2 lg:order-none">
              {settings.dashboardItems.dateTime && (
                <DateTimeCard 
                  dateTime={dashboardData.dateTime} 
                  prayerTimes={dashboardData.prayerTimes}
                  isLoading={isLoading}
                  className="space-y-1"
                  timeClassName="text-4xl font-medium text-white"
                  untilClassName="text-md text-gray-300"
                  dateClassName="text-sm text-white/80"
                  dividerClassName="text-white/40"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content - Background wrapper removed */}
      {/* The complex background is now handled conditionally in Layout.js */}
      <div className="relative z-10 space-y-8 pb-8"> {/* Added pb-8 for spacing, removed background wrapper */}
        {/* Prayer Times, Quran Progress, and Goals Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {settings.dashboardItems.prayerTimes && (
            // Card wrapper styling remains
            <div className={`relative rounded-2xl overflow-hidden backdrop-blur-xl p-6 ${
              isDarkMode 
                ? 'bg-white/5 border border-white/10' 
                : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)] border border-black/5'
            }`}>
              <PrayerTimesCard 
                prayerTimes={dashboardData.prayerTimes} 
                error={dashboardData.prayerTimesError}
                isLoading={
                  // ONLY show loading state if we have coordinates/location but are waiting for prayer times
                  settings.coordinates && 
                  settings.location && 
                  (!dashboardData?.prayerTimes || 
                   !dashboardData?.prayerTimes.times || 
                   Object.keys(dashboardData?.prayerTimes?.times || {}).length === 0)
                }
              />
            </div>
          )}
          
          {isInitialized && settings.dashboardItems.quranTracker && (
            <div className={`relative rounded-2xl overflow-hidden backdrop-blur-xl p-6 ${
              isDarkMode 
                ? 'bg-white/5 border border-white/10' 
                : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)] border border-black/5'
            }`}>
              <AnimatePresence mode="wait">
                {currentUser ? (
                  <QuranProgressCard key="progress-logged-in" isLoading={isLoading} />
                ) : (
                  showGuestProgress ? (
                    <motion.div
                      key="progress-guest"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      <QuranProgressCard isLoading={isLoading} />
                    </motion.div>
                  ) : (
                    <QuranGuestCard 
                      key="guest-cta"
                      isDarkMode={isDarkMode} 
                      showNoThanksButton={true}
                      onNoThanks={handleGuestNoThanks}
                    />
                  )
                )}
              </AnimatePresence>
            </div>
          )}

          {isInitialized && settings.dashboardItems.goals && currentUser && (
            <div className={`relative rounded-2xl overflow-hidden backdrop-blur-xl p-6 ${
              isDarkMode 
                ? 'bg-white/5 border border-white/10' 
                : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)] border border-black/5'
            }`}>
              <GoalsCard goals={dashboardData.goals} isLoading={isLoading} />
            </div>
          )}
        </div>

        {/* Show loading indicator if settings aren't initialized */}
        {!isInitialized && (
          <div className="flex flex-col min-h-[200px] items-center justify-center">
            <div className="w-8 h-8 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mb-4" />
            <span className="text-gray-500 dark:text-gray-400 text-sm">Loading remaining content...</span>
          </div>
        )}

        {/* Daily Guidance Section - Only show when settings are initialized */}
        {isInitialized && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {settings.dashboardItems.dailyDua && (
              // Card wrapper styling remains
              <div className={`relative rounded-2xl overflow-hidden backdrop-blur-xl p-6 ${
                isDarkMode 
                  ? 'bg-white/5 border border-white/10' 
                  : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)] border border-black/5'
              }`}>
                <DailyDuaCard 
                  dailyDua={dashboardData?.duas?.[dashboardData?.currentDuaIndex]} 
                  onNext={handleNextDua}
                  onPrevious={handlePreviousDua}
                  isLoading={!dashboardData?.duas || dashboardData?.duas.length === 0}
                  noCard={true}
                />
              </div>
            )}
            
            {settings.dashboardItems.dailyHadith && (
              // Card wrapper styling remains
              <div className={`relative rounded-2xl overflow-hidden backdrop-blur-xl p-6 ${
                isDarkMode 
                  ? 'bg-white/5 border border-white/10' 
                  : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)] border border-black/5'
              }`}>
                <DailyHadithCard 
                  dailyHadith={dashboardData?.hadiths?.[dashboardData?.currentHadithIndex]} 
                  onNext={handleNextHadith}
                  onPrevious={handlePreviousHadith}
                  isLoading={!dashboardData?.hadiths || dashboardData?.hadiths.length === 0}
                  error={dashboardData?.hadithsError}
                  noCard={true}
                />
              </div>
            )}
          </div>
        )}

        {/* Additional Features Section - Only show when settings are initialized */}
        {isInitialized && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> {/* Removed pb-8 from here */}
            {settings.dashboardItems.adhkarTracker && (
              // Card wrapper styling remains
              <div className={`relative rounded-2xl overflow-hidden backdrop-blur-xl p-6 ${
                isDarkMode 
                  ? 'bg-white/5 border border-white/10' 
                  : 'bg-white/60 shadow-[inset_0_0_1px_1px_rgba(255,255,255,0.9)] border border-black/5'
              }`}>
                <AdhkarTrackerCard 
                  adhkarCompleted={dashboardData.adhkarCompleted} 
                  onToggleAdhkar={handleToggleAdhkar}
                  isLoading={!dashboardData?.adhkarCompleted}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopLayout; 